import { useEffect, useState, useRef } from "react";
import {
  Moon,
  Bed,
  Cussion,
  GreenThumb,
  LockSleepCoach,
} from "@utils/defaultImages.js";
import stepBack from "@assets/images/stepsBack.jpg";
import Rank3 from "@assets/images/Ranks3.svg";
import SleepCoachCharts from "./DashboardCharts";
import "swiper/css";
import "swiper/css/navigation";
import DashboardSuggestion from "./DashboardSuggestion";
import HeaderImage from "@assets/images/SleepCoachHeader.svg";
import MainHeader from "@components/ui/MainHeader.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Education from "./Education.jsx";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader.jsx";
import { useUserData } from "context/OAuth/AuthContext.js";
import { WhiteDownload } from "@utils/defaultImages";
import { fetchSleepData } from "api/sleepCoach.js";
import { toast } from "react-toastify";
import ReacreationLoading from "@components/ui/RecreationLoading.jsx";
import { useLoaderSlides } from "data/uiData/LoadersData.js";
import RankUnlockedModal from "@components/common/FramerMotionModal.jsx";
import SleepCoachMilestoneModal from "@features/sleep/SleepCoachMilestoneModal.jsx";
import usePlanTimer from "@utils/DietPlanProgress.js";

const SleepCoachDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDay, setSelectedDay] = useState(() => dayjs().format("dddd"));
  const [adviseData, setAdviseData] = useState(null);
  const [todayAdviseData, setTodayAdviseData] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [badgeLocked, setBadgeLocked] = useState(true);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [openAchivementModal, setOpenAchivementModal] = useState(false);

  const [graphData, setGraphData] = useState(null);
  const { t } = useTranslation();
  const { language, subscription } = useUserData();
  const [planInitiateTime, setPlanInitiateTime] = useState(null);

  const { timeLeft, setTimeLeft, stopCountdown } = usePlanTimer(
    planInitiateTime,
    120
  );
  const todayName = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const isMountedRef = useRef(true);
  const sleepSlides = useLoaderSlides("sleep");

  useEffect(() => {
    if (!location?.state) {
      navigate(-1);
    }
  }, [location, navigate]);

  const handleSetData = (response = null) => {
    const data = response?.data?.data;
    const todayAdvise = data?.advice?.filter((item) => item?.type !== "weekly");
    const weeklyData = data?.advice?.find((item) => item?.day === todayName);
    const mileStoneAchieved = data?.is_sleep_milestone_achieved;
    setPlanInitiateTime(response?.data?.start_time);
    setOpenAchivementModal(data?.milestone_popup_show);
    setTrackingData(data);
    setGraphData(data?.sleep_graph_data);
    setAdviseData(todayAdvise);
    setTodayAdviseData(weeklyData);
    setBadgeLocked(!mileStoneAchieved);
  };

  const fetchStepsData = async (showToast) => {
    try {
      const response = await fetchSleepData();

      const loadingStatus = response?.data?.loading;

      if (!response?.data?.data && !loadingStatus) {
        toast.error(t("Something went wrong. Redirecting you to try again..."));
        navigate("/sleep-coach");
        return;
      }
      handleSetData(response);
      setIsPlanLoading(loadingStatus);

      setTimeout(() => {
        setFirstLoad(false);
      }, 1000);
    } catch (error) {
      console.error(
        "Error fetching Data:",
        error.response ? error.response.data : error.message
      );
      if (isMountedRef.current) {
        toast.error(t("Something went wrong. Redirecting you to try again..."));
        navigate("/sleep-coach");
        setFirstLoad(false);
      }
    }
  };

  const handleFailedPlan = () => {
    // toast.error(t("Failed to create plan! , please try again."));
    if (adviseData) {
      setIsPlanLoading(false);
    } else {
      navigate("/sleep-coach");
      return;
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchStepsData();
  }, []);

  // Cleanup effect when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const downloadPDF = () => {
    try {
      const linkToDownload =
        language === "nl"
          ? "/sleepCoachArtichle-Dutch.pdf"
          : "/sleepCoachArtichle.pdf";
      const link = document.createElement("a");
      link.href = linkToDownload;
      link.download = `${t("sleep_coach_artichle")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.info(t("toast.Article Downloaded Successfully!"));
    } catch (error) {
      console.log("Failed to download artichle", error);
      toast.error(t("toast.Failed to download the article. Please try again"));
    }
  };

  const nutrientData = [
    {
      label: t("sleepCoachPreferences.avgSleepDuration"),
      key: "average_sleep_duration",
      icon: <Moon className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#F0FFFF",
      value: trackingData?.average_sleep_duration ?? "0h 0min",
    },
    {
      label: t("sleepCoachPreferences.wakeUpsPerWeek"),
      key: "wake_ups_per_week",
      icon: <Bed className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#FFF0FB",
      value: `${trackingData?.wake_ups_per_week ?? 0} ${t(
        "sleepCoachPreferences.nights"
      )}`,
    },
    {
      label: t("sleepCoachPreferences.sleepConsistency"),
      key: "sleep_consistency_days",
      icon: <Cussion className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#FFF6F0",
      value: `${trackingData?.sleep_consistency_days ?? 0} ${t(
        "sleepCoachPreferences.days"
      )}`,
    },
    {
      label: t("sleepCoachPreferences.adviceFollowed"),
      key: "advice_followed",
      icon: <GreenThumb className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#F5FFF0",
      value: `${trackingData?.advice_followed?.days_followed ?? 0} of ${
        adviseData?.length ?? 7
      } ${t("sleepCoachPreferences.days")}`,
    },
  ];

  const data = {
    RankImage: Rank3,
    title: t("sleepCoachPreferences.Sleep Consistency Streak"),
    unlockDate: "Unlocked on 5th July",
    description: t(
      "sleepCoachPreferences.You've achieved 7 consecutive nights of consistent sleep, a big step toward healthier rest habits!"
    ),
  };
  if (location?.state) {
    return (
      <div>
        {firstLoad ? (
          <ActualDietPlanLoader sleepCoach={true} />
        ) : !adviseData && isPlanLoading ? (
          <ReacreationLoading
            timeLeft={timeLeft}
            minuteSet={120}
            data={sleepSlides}
            type="sleep"
            loaderHeading={t("SleepCoachLoader.mealPlanProgress")}
            loaderSubheading={t("SleepCoachLoader.analyzingPlan")}
            onComplete={() => fetchStepsData(true)}
            onFailed={handleFailedPlan}
            stopCountdown={() => stopCountdown()}
            setTimeLeft={setTimeLeft}
          />
        ) : (
          <>
            {adviseData && (
              <div>
                <MainHeader
                  heading={t(
                    "sleepCoachPreferences.sleepCoachDashboardHeading"
                  )}
                  subheading={t(
                    "sleepCoachPreferences.sleepCoachDashboardSubHeading"
                  )}
                  description={t("sleepCoachPreferences.sleepCoachDescription")}
                  Image={HeaderImage}
                />
                {isPlanLoading && (
                  <div className="mt-6">
                    <ReacreationLoading
                      timeLeft={timeLeft}
                      minuteSet={120}
                      data={sleepSlides}
                      type="sleep"
                      loaderHeading={t("SleepCoachLoader.mealPlanProgress")}
                      loaderSubheading={t("SleepCoachLoader.analyzingPlan")}
                      onComplete={() => fetchStepsData(true)}
                      onFailed={handleFailedPlan}
                      stopCountdown={() => stopCountdown()}
                      setTimeLeft={setTimeLeft}
                      recreateLoading={true}
                    />
                  </div>
                )}
                <div className="flex justify-end gap-2 mt-9">
                  <button
                    onClick={() => downloadPDF()}
                    className={`px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[12px] flex items-center justify-center gap-2 text-white font-[600] font-[Lato]`}
                  >
                    <WhiteDownload />
                    {t("sleepCoachPreferences.Sleep Help Guide (PDF)")}
                  </button>
                  {!isPlanLoading && (
                    <button
                      disabled={isPlanLoading}
                      onClick={() =>
                        subscription?.status === "active" ||
                        subscription?.status === "pending-cancel"
                          ? navigate("/sleep-coach-preferences", {
                              state: {
                                sleepCoach: true,
                              },
                            })
                          : navigate("/renew-subscription")
                      }
                      className={`${
                        isPlanLoading && "opacity-70"
                      } px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-[12px] font-[600] font-[Lato]`}
                    >
                      {t("Recreate Plan")}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 mb-9">
                  {nutrientData.map(
                    ({ label, icon, value, tabColor }, index) => (
                      <div
                        key={index}
                        className="py-8 px-2 rounded-[17px] bg-white border border-[#E6E6E6]  rounded-[16px] flex items-center flex-col xl:flex-row  justify-center gap-2"
                        style={{
                          boxShadow: "7px 7px 9.5px 0px #0000000D",
                        }}
                      >
                        <div className="flex justify-center">
                          <div
                            className="rounded-full min-w-[64px] min-h-[64px] max-w-[64px] max-h-[64px] flex items-center justify-center"
                            style={{
                              backgroundColor: tabColor,
                            }}
                          >
                            {icon}
                          </div>
                        </div>
                        <div>
                          <p className="text-[12px] md:text-[15px] font-[500] text-[#898787] font-[Lato] text-center md:text-start lg:text-center xl:text-start">
                            {label}
                          </p>
                          <div className="">
                            <p className="text-[16px] md:text-[22px] font-[500] font-[Lato] text-[#6C6A6A] text-center lg:text-center xl:text-start">
                              {value}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-7 gap-5">
                  <div className="xl:col-span-7">
                    <DashboardSuggestion
                      adviseData={adviseData}
                      todayAdviseData={todayAdviseData}
                      selectedDay={selectedDay}
                      setSelectedDay={setSelectedDay}
                      handleSetData={handleSetData}
                    />
                  </div>

                  <div className="xl:col-span-4">
                    <Education />
                  </div>

                  <div className="xl:col-span-3">
                    <div
                      className="p-6 col-span-3 rounded-xl text-white bg-cover xl:h-full relative" // Added relative here
                      style={{ backgroundImage: `url(${stepBack})` }}
                    >
                      <h4 className="text-[16px] font-[Lato] text-white mb-10">
                        {t("sleepCoachPreferences.Milestones Achieved")}
                      </h4>

                      <div className="flex justify-center  w-full mb-10">
                        <div
                          className={`${
                            badgeLocked && "blur-[8px]"
                          } bg-white rounded-3xl w-[85%] p-5 relative pb-8`}
                        >
                          {" "}
                          <div className="flex justify-center mt-[35%] sm:mt-[18%] lg:mt-[25%] xl:mt-[30%]">
                            <h3
                              className={`${
                                badgeLocked
                                  ? "text-white bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] w-[fit-content] py-2 px-3 my-2"
                                  : "text-[#000]"
                              } text-[16px] font-[600] font-[Lato] text-center`}
                            >
                              {t(
                                "sleepCoachPreferences.Sleep Consistency Streak"
                              )}
                            </h3>
                          </div>
                          <div className="flex justify-center">
                            {!badgeLocked && (
                              <button
                                disabled
                                className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] py-2 px-4 text-[12px] my-2"
                              >
                                {badgeLocked
                                  ? "Locked"
                                  : t("sleepCoachPreferences.unlocked")}
                              </button>
                            )}
                          </div>
                          <div className="border border-[#DDDDDD] p-2 rounded-xl">
                            <p className="text-[14px] text-[#5C5C5C] text-center">
                              {t(
                                "sleepCoachPreferences.You've achieved 7 consecutive nights of consistent sleep, a big step toward healthier rest habits!"
                              )}
                            </p>
                          </div>
                          <img
                            src={Rank3}
                            alt="image"
                            className={`absolute -top-[8%] left-1/2 -translate-x-1/2 z-[9] w-[40%] sm:w-[25%] md:w-[25%] lg:w-[30%] xl:w-[40%] object-cover" `} // Adjusted positioning and added width
                            style={{ maxHeight: "none" }}
                          />
                        </div>
                      </div>

                      {badgeLocked && (
                        <img
                          src={LockSleepCoach}
                          alt="image"
                          className="absolute top-[40%] 2xl:top-[30%]  left-1/2 -translate-x-1/2 z-[9] object-cover w-[40px] sm:w-[50px]"
                        />
                      )}
                    </div>
                  </div>

                  <div className="xl:col-span-7">
                    <SleepCoachCharts graphData={graphData} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <SleepCoachMilestoneModal
          isOpen={openAchivementModal}
          setIsOpen={setOpenAchivementModal}
          item={data}
        />
      </div>
    );
  }
};

export default SleepCoachDashboard;
